import { Button } from 'components/ui/button';
import { buttonStyle } from 'components/buttons/buttonStyle';
import { useCallback, useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import SelectInput from 'components/forms/inputs/rhf-inputs/SelectInput';
import TextAreaInput from 'components/forms/inputs/rhf-inputs/TextAreaInput';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import { HandleInvoiceTransitionArgs } from 'pages/invoices/InvoiceWorkflow';
import { InvoiceData, InvoiceStatusType, InvoiceTransactionActionType } from 'types/invoices';
import { t } from 'utils/getTranslations';
import { ModalFooterSimple } from '../ModalFooterSimple';
import { ModalHeader } from '../ModalHeader';
import { predefinedReasons } from './invoicePredefinedReasons';

interface Props {
    onClose: () => void;
    onSuccess?: () => void;
    invoice: InvoiceData;
    isRequesting?: boolean;
    reason?: string;
    handleStatusChange: (args: HandleInvoiceTransitionArgs) => Promise<void>;
}

interface InvoicePostponeFormData {
    predefinedReason: { id: number; name: string } | null;
    comment: string | null;
}

const defaultValues: InvoicePostponeFormData = {
    predefinedReason: null,
    comment: null,
};

const InvoicePostponeFormModal = (props: Props) => {
    const { onClose, onSuccess, invoice, isRequesting = false, handleStatusChange, reason = '' } = props;

    const {
        handleSubmit: handleFormSubmit,
        control,
        setValue,
    } = useForm<InvoicePostponeFormData>({
        defaultValues: defaultValues,
    });

    // TODO(react-doctor: no-effect-event-handler): seeded by `reason` prop on mount;
    // RHF `setValue` is a framework API, leave in effect.
    useEffect(() => {
        const predefinedReason = predefinedReasons.find((value) => reason.includes(value.name));
        if (predefinedReason) {
            setValue('predefinedReason', predefinedReason);
            const userComment = reason.replace(predefinedReason.name, '').replace('.', '').trim();
            setValue('comment', userComment);
        } else {
            setValue('comment', reason);
        }
    }, [reason, setValue]);

    const predefinedReason = useWatch({ control, name: 'predefinedReason' });

    // TODO(react-doctor: no-effect-event-handler): triggered by the watched
    // `predefinedReason`; the change source is the controlled <select> via RHF.
    useEffect(() => {
        if (predefinedReason) {
            setValue('comment', `${predefinedReason.name}.`);
        }
    }, [predefinedReason, setValue]);

    const handleSubmit = useCallback(
        async (status: InvoiceStatusType) => {
            await handleFormSubmit(async (formData) => {
                await handleStatusChange({
                    to: status,
                    transactionActionType: InvoiceTransactionActionType.POSTPONED,
                    comment: formData.comment || '',
                });
                onClose();
                onSuccess?.();
            })();
        },
        [handleFormSubmit, handleStatusChange, onClose, onSuccess]
    );

    const isNew = useIsNewDesign();

    return (
        <>
            <ModalHeader
                kit
                title={t(isRequesting ? 'invoices.form.request-postpone' : 'invoices.form.postpone-invoice', {
                    number: invoice.invoiceNumber,
                })}
                description={t(
                    isRequesting ? 'invoices.form.request-postpone-description' : 'invoices.form.postpone-description'
                )}
                onCancel={onClose}
            />
            <div className="h-full overflow-y-auto p-6 max-sm:px-4">
                <div className="grid gap-4">
                    {/* The new design says this in the header's description. */}
                    {!isNew && (
                        <p className="text-body1 mb-3">
                            <FormattedMessage
                                id={
                                    isRequesting
                                        ? 'invoices.form.request-postpone-invoice-number'
                                        : 'invoices.form.postpone-invoice-number'
                                }
                                values={{ number: invoice.invoiceNumber }}
                            />
                        </p>
                    )}
                    <SelectInput
                        setValue={setValue}
                        control={control}
                        label="invoices.form.select-predefined-reason"
                        name="predefinedReason"
                        options={predefinedReasons}
                        getOptionLabel="name"
                        getOptionIdentifier="id"
                    />
                    <TextAreaInput
                        name="comment"
                        control={control}
                        label="invoices.form.add-specific-reason"
                        minRows={isNew ? 3 : undefined}
                    />
                    {/* Skipping “Next invoice date” at the moment */}
                    {/* <DateInput control={control} name="nextInvoiceDate" label="invoices.form.next-invoice-date" /> */}
                </div>
            </div>
            <ModalFooterSimple
                kit
                buttons={[
                    <Button {...buttonStyle({ variant: 'outlined', color: 'primary' })} key="cancel" onClick={onClose}>
                        {t('cancel')}
                    </Button>,
                    <Button
                        {...buttonStyle({ variant: 'contained', color: 'secondary' })}
                        key="postpone"
                        onClick={() => {
                            void handleSubmit(isRequesting ? InvoiceStatusType.REVIEWED : InvoiceStatusType.POSTPONED);
                        }}
                    >
                        {t(
                            !isNew
                                ? 'save'
                                : isRequesting
                                  ? 'invoices.actions.request-postpone'
                                  : 'invoices.actions.postpone'
                        )}
                    </Button>,
                ]}
            />
        </>
    );
};

export default InvoicePostponeFormModal;
